@use 'src/globals/main';
@use 'sass:string';

@mixin date-today {
  font-weight: 500;
  &::after {
    content: '';
    position: absolute;
    bottom: 0.25em;
    left: calc(50% - 0.125em);
    height: 0.25em;
    width: 0.25em;
    background-color: main.$blue700;
    border-radius: 50%;
  }
}

pn-date-picker {
  position: relative;
  display: block;
}

pn-date-picker .pn-date-picker {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25em;

  container-type: inline-size;

  @container (max-width: 24em) {
    .pn-date-picker-container {
      flex: 1 1 20em;
    }
    .pn-date-picker-range-icon {
      display: none;
    }
  }

  &-container {
    display: block;
    flex: 1 1 11em;

    &[data-error] {
      > .pn-date-picker-label {
        color: main.$warning;
      }
      > .pn-date-picker-field .pn-date-picker-input {
        @include main.pn-input-field-error;
      }
    }
  }

  &-label {
    @include main.pn-input-label;
  }

  &-field {
    position: relative;
    display: flex;
  }

  &-input {
    @include main.pn-input-field;
    padding-right: 3em;
    flex: auto;
    width: 100%;

    &::-webkit-calendar-picker-indicator {
      visibility: hidden;
      inline-size: 0;
    }

    &::-webkit-textfield-decoration-container,
    &::-webkit-search-cancel-button,
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button,
    &::-webkit-search-decoration {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    &[data-active] {
      border-color: main.$border-color-default-active;
    }
    &:read-only {
      @include main.pn-readonly;
    }
  }

  &-range-icon {
    width: 1.5em;
    margin-bottom: 0.8125em;
    align-self: end;
  }

  &-helpertext,
  &-error {
    @include main.pn-input-helpertext;
    text-align: left;
    &[hidden] {
      display: none;
    }
  }

  &-error {
    color: main.$warning;
  }

  &-toggle {
    position: absolute;
    top: 0.5em;
    right: 0.5em;
    border-radius: 50%;
  }

  &-button {
    cursor: pointer;
    font-size: 1em;
    background-color: transparent;
    border: main.$input-border;
    border-color: transparent;
    padding: 0.25em;
    margin: 0;
    flex: auto;
    border-radius: 3em;

    &:first-child,
    &:last-child {
      flex: 0 0 auto;
    }

    @include main.pn-transition('outline-color, background-color, border-color');
    @include main.pn-focus-base();

    &:focus {
      background-color: main.$white;
      @include main.pn-focus-visible();
    }

    &:hover {
      background-color: main.$input-button-background-hover;
    }

    &[data-active],
    &[data-range] {
      background-color: main.$input-button-background-active;
      border-color: main.$blue700;
    }

    &:disabled {
      cursor: not-allowed;
      &:hover {
        background-color: transparent;
      }
    }
  }

  &-month {
    text-transform: capitalize;
    &[data-full] {
      display: none;
      @media (min-width: 25em) {
        display: inline;
      }
    }
    &[data-abbr] {
      display: inline;
      @media (min-width: 25em) {
        display: none;
      }
    }
  }

  @include main.pn-sr-only-class();

  /** The calendar dropdown */
  &-calendar {
    position: absolute;
    z-index: 4;
    top: calc(100% + 0.5em);

    left: var(--pn-calendar-offset-left);
    width: clamp(100%, 95vw, 20em);
    max-width: 20em;

    text-align: center;

    border-radius: main.$border-radius;
    background-color: main.$white;

    overflow: hidden;
    visibility: hidden;
    box-shadow: 0 0.25em 0.5em rgba(0, 0, 0, 0.1);

    &[data-range] {
      width: 100%;
      max-width: unset;
    }

    &[data-open],
    &[data-moving] {
      visibility: visible;
    }

    &[data-direction='top'] {
      top: unset;
      bottom: calc(100% + 0.5em);
      transform-origin: bottom left;
    }

    pn-button[hidden] {
      display: none;
    }

    pn-button span {
      text-transform: capitalize;
    }
  }

  &-wrapper {
    padding: 0.5em;
  }

  &-title {
    margin: 0.5em auto 0;
    font-size: 0.875em;
    font-weight: 500;
    color: main.$gray900;
  }

  &-nav {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 1em;
  }

  &-table {
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border: 0;
    margin: 0 0 0.5em;
  }

  &-th {
    font-size: 0.75em;
    font-weight: 500;
    color: main.$helpertext-color;
    padding: 0.25em;
    border: 0;
    text-transform: capitalize;
  }

  &-td {
    cursor: pointer;
    position: relative;
    padding: 0.25em;
    outline: 0;
    border: 0;
    line-height: 0;
    width: 2em;
    height: 2em;
    -webkit-tap-highlight-color: transparent;

    &-text {
      position: relative;
      display: block;
      line-height: 1.5em;
      border-radius: main.$border-radius;
      color: main.$input-color;
      padding: 0.25em;

      @include main.pn-transition('color, background-color, outline-color');
      @include main.pn-focus-base;
    }

    &[data-blank] {
      cursor: auto;
      .pn-date-picker-td-text {
        background-color: transparent;
      }
    }

    &-week {
      font-size: 0.75em;
      color: main.$gray700;
      display: block;
    }

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.25em;
      height: calc(100% - 0.5em);
      width: 100%;
      background-color: transparent;
      border: 0.0625em solid transparent;
      border-radius: main.$border-radius;
      @include main.pn-transition('border-color, background-color, width, transform, left');
    }

    &[aria-disabled='true'] {
      cursor: not-allowed;
      pointer-events: none;
      > .pn-date-picker-td-text {
        color: main.$input-color-disabled;
        background-color: main.$input-background-disabled;
      }
      &:focus > .pn-date-picker-td-text {
        @include main.pn-focus-visible(main.$warning);
      }
      &:hover,
      &:focus {
        > .pn-date-picker-td-text {
          background-color: main.$input-background-disabled;
        }
      }
    }

    &[aria-selected='true'] {
      .pn-date-picker-td-text {
        font-weight: 500;
      }
      &:before {
        border-color: main.$blue700;
        background-color: main.$blue25;
      }
      &[data-single]:before {
        width: 2em;
        left: 50%;
        transform: translateX(-50%);
      }
      &[data-range]:before {
        border-radius: 0;
        border-left: 0;
        border-right: 0;
      }
      &[data-range-from]:before {
        border-radius: main.$border-radius 0 0 main.$border-radius;
        border-right-width: 0;
        border-left-width: 0.0625em;
      }
      &[data-range-to]:before {
        border-radius: 0 main.$border-radius main.$border-radius 0;
        border-right-width: 0.0625em;
        border-left-width: 0;
      }
      &[aria-disabled='true'] {
        &:before {
          border-color: main.$warning;
          background-color: main.$coral50;
        }
        > .pn-date-picker-td-text {
          background-color: unset;
        }
        &:focus > .pn-date-picker-td-text {
          background-color: main.$input-background-disabled;
        }
      }
    }

    &[aria-selected='false']:hover > .pn-date-picker-td-text {
      background-color: main.$input-button-background-hover;
    }

    &[data-today] > .pn-date-picker-td-text {
      @include date-today;
      &:after {
        bottom: 0.15em;
      }
    }

    &[data-active][data-today] > .pn-date-picker-td-text:after {
      background-color: main.$blue700;
    }

    &:focus-visible > .pn-date-picker-td-text {
      @include main.pn-focus-visible(main.$blue700);
    }
  }

  /* Month list */
  &-list {
    margin: 0 0 1em;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 0.5em;
  }

  &-item {
    > .pn-date-picker-button {
      display: block;
      width: 100%;
      border-radius: main.$border-radius;
      padding: 0.5em 0.25em;
      &[data-today] {
        position: relative;
        @include date-today;
      }
    }
  }

  &-chips > [slot='chips'] {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 0.5em;
    margin-bottom: 0.5em;
    &:empty {
      display: none;
    }
  }

  &-bottom {
    display: flex;
    justify-content: flex-end;
    gap: 0.5em;
    &[hidden] {
      display: none;
    }
  }
}
